import {
  collection,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  increment,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  Timestamp,
  writeBatch,
  startAfter
} from '@firebase/firestore';
import { db } from '../config/firebase';
import { Poll } from '../types/firebase';
import { User as FirebaseUser } from 'firebase/auth';

const pollsCollection = collection(db, 'polls');

export const createPoll = async (
  data: Omit<Poll, 'id' | 'createdAt' | 'updatedAt' | 'totalVotes' | 'status'>,
  user: FirebaseUser
): Promise<Poll> => {
  try {
    const now = serverTimestamp();
    // Ensure options are properly structured
    const options = data.options.map(option => ({
      id: option.id,
      text: option.text,
      votes: 0,
      votedBy: []
    }));

    // Base poll data
    const pollData = {
      title: data.title,
      description: data.description,
      cause: data.cause,
      createdAt: now,
      updatedAt: now,
      endDate: data.endDate,
      createdBy: {
        id: user.uid,
        name: user.displayName || 'Anonymous',
        avatar: user.photoURL || ''
      },
      totalVotes: 0,
      status: 'active',
      options: options,
      settings: {
        allowComments: data.settings?.allowComments ?? true
      }
    };

    // Remove group poll logic, only handle regular polls
    const finalPollData = {
      ...pollData,
      visibility: 'public'
    };

    console.log('Creating poll with data:', finalPollData);

    const batch = writeBatch(db);

    // Create the poll document
    const pollRef = doc(pollsCollection);
    batch.set(pollRef, finalPollData);

    // Update user's stats
    const userRef = doc(db, 'users', user.uid);
    batch.update(userRef, {
      'stats.pollsCreated': increment(1),
      lastActive: now
    });

    await batch.commit();
    return {
      id: pollRef.id,
      ...finalPollData,
      options: options
    } as Poll;
  } catch (error) {
    console.error('Error creating poll:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to create poll');
  }
};

export const getPolls = async (
  cause?: string,
  sortBy: 'latest' | 'votes' = 'latest',
  status?: Poll['status'],
  pageSize = 10,
  lastDoc?: any
): Promise<{ polls: (Poll & { __snapshot?: any })[]; lastVisible: any }> => {
  try {
    let baseQuery = query(
      pollsCollection,
      orderBy('createdAt', 'desc')
    );
    if (cause) {
      baseQuery = query(baseQuery, where('cause', '==', cause));
    }
    if (status) {
      baseQuery = query(baseQuery, where('status', '==', status));
    }
    baseQuery = query(baseQuery, limit(pageSize));
    if (lastDoc) {
      baseQuery = query(baseQuery, startAfter(lastDoc));
    }
    const snapshot = await getDocs(baseQuery);
    const polls = snapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          settings: {
            allowComments: data.settings?.allowComments ?? true
          },
          __snapshot: doc,
        } as Poll & { __snapshot?: any };
      })
      // Remove groupId filtering, just return all polls
      .sort((a, b) => {
        if (sortBy === 'votes') {
          return (b.totalVotes || 0) - (a.totalVotes || 0);
        }
        return 0;
      });
    const lastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    return { polls, lastVisible };
  } catch (error) {
    console.error('Error getting polls:', error);
    throw error;
  }
};

export const getPollById = async (pollId: string): Promise<Poll> => {
  try {
    const pollDoc = await getDoc(doc(pollsCollection, pollId));
    if (!pollDoc.exists()) {
      throw new Error('Poll not found');
    }
    return {
      id: pollDoc.id,
      ...pollDoc.data()
    } as Poll;
  } catch (error) {
    console.error('Error getting poll:', error);
    throw error;
  }
};

export const updatePoll = async (
  pollId: string,
  data: Partial<Poll>,
  userId: string
): Promise<void> => {
  try {
    const pollRef = doc(pollsCollection, pollId);
    const pollDoc = await getDoc(pollRef);
    
    if (!pollDoc.exists()) {
      throw new Error('Poll not found');
    }

    const poll = pollDoc.data() as Poll;
    if (poll.createdBy.id !== userId) {
      throw new Error('Unauthorized to update poll');
    }

    await updateDoc(pollRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating poll:', error);
    throw error;
  }
};

export const deletePoll = async (pollId: string, userId: string): Promise<void> => {
  try {
    const pollRef = doc(db, 'polls', pollId);
    const pollDoc = await getDoc(pollRef);

    if (!pollDoc.exists()) {
      throw new Error('Poll not found');
    }

    const poll = pollDoc.data() as Poll;

    // Check authorization
    if (poll.createdBy.id !== userId) {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const isAdmin = userDoc.exists() && userDoc.data()?.role === 'admin';
      
      if (!isAdmin) {
        throw new Error('Unauthorized to delete this poll');
      }
    }

    // Delete the poll document
    await deleteDoc(pollRef);

    // Update creator's stats
    const creatorRef = doc(db, 'users', poll.createdBy.id);
    await updateDoc(creatorRef, {
      'stats.pollsCreated': increment(-1)
    });

    console.log('Successfully deleted poll');
  } catch (error) {
    console.error('Error deleting poll:', error);
    throw error;
  }
};

export const votePoll = async (
  pollId: string,
  optionId: string,
  userId: string
): Promise<void> => {
  try {
    console.log('Voting on poll:', { pollId, optionId, userId });
    
    const pollRef = doc(pollsCollection, pollId);
    const pollDoc = await getDoc(pollRef);
    
    if (!pollDoc.exists()) {
      throw new Error('Poll not found');
    }

    const poll = pollDoc.data() as Poll;
    console.log('Current poll data:', poll);
    
    // Check if poll has ended
    if (poll.status === 'ended') {
      throw new Error('This poll has ended');
    }

    // Check if end date has passed
    if (poll.endDate && (poll.endDate as Timestamp).toDate() < new Date()) {
      await updateDoc(pollRef, {
        status: 'ended',
        updatedAt: serverTimestamp()
      });
      throw new Error('This poll has ended');
    }

    // Find the option index
    const optionIndex = poll.options.findIndex(opt => opt.id === optionId);
    if (optionIndex === -1) {
      throw new Error('Invalid option');
    }

    // Check if user has already voted in any option
    const hasVoted = poll.options.some(option => 
      Array.isArray(option.votedBy) && option.votedBy.includes(userId)
    );
    
    if (hasVoted) {
      throw new Error('You have already voted in this poll');
    }

    // Create a batch write
    const batch = writeBatch(db);

    // Update the poll document
    batch.update(pollRef, {
      [`options.${optionIndex}.votes`]: increment(1),
      [`options.${optionIndex}.votedBy`]: arrayUnion(userId),
      totalVotes: increment(1),
      updatedAt: serverTimestamp()
    });

    // Update user's stats
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
      'stats.pollVotes': increment(1),
      lastActive: serverTimestamp()
    });

    // Commit both updates atomically
    await batch.commit();

    // Verify the update
    const updatedDoc = await getDoc(pollRef);
    const updatedData = updatedDoc.data();
    console.log('Updated poll data:', updatedData);

    // If options are missing or malformed, fix them
    if (updatedData && (!updatedData.options || !Array.isArray(updatedData.options) || updatedData.options.length === 0)) {
      const fixedOptions = poll.options.map((opt, idx) => ({
        ...opt,
        votes: idx === optionIndex ? opt.votes + 1 : opt.votes,
        votedBy: idx === optionIndex ? [...opt.votedBy, userId] : opt.votedBy
      }));
      
      await updateDoc(pollRef, {
        options: fixedOptions
      });
      
      console.log('Fixed missing options:', fixedOptions);
    }
  } catch (error) {
    console.error('Error voting in poll:', error);
    throw error;
  }
};

export const removeVote = async (
  pollId: string,
  optionId: string,
  userId: string
): Promise<void> => {
  try {
    const pollRef = doc(pollsCollection, pollId);
    const pollDoc = await getDoc(pollRef);
    
    if (!pollDoc.exists()) {
      throw new Error('Poll not found');
    }

    const poll = pollDoc.data() as Poll;
    
    // Check if poll has ended
    if (poll.status === 'ended') {
      throw new Error('This poll has ended');
    }

    const optionIndex = poll.options.findIndex(opt => opt.id === optionId);
    if (optionIndex === -1) {
      throw new Error('Invalid option');
    }

    // Check if user has voted for this option
    if (!poll.options[optionIndex].votedBy.includes(userId)) {
      throw new Error('You have not voted for this option');
    }

    // Remove the vote
    await updateDoc(pollRef, {
      [`options.${optionIndex}.votes`]: increment(-1),
      [`options.${optionIndex}.votedBy`]: arrayRemove(userId),
      totalVotes: increment(-1),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error removing vote:', error);
    throw error;
  }
}; 